import { useEffect, useRef, useState, type ReactNode, type MouseEvent } from 'react';

import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';

import { cn, motionTransition } from '../../utils';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  position?: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'responsive-lg';
  showBackdrop?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  children: ReactNode;
  className?: string;
}

const sizeStyles = {
  sm: 'w-64',
  md: 'w-80',
  lg: 'w-96',
  xl: 'w-[420px]',
  full: 'w-screen',
  'responsive-lg': 'w-screen md:w-96', // Full width on mobile, lg on desktop
};

export function Drawer({
  isOpen,
  onClose,
  position = 'left',
  size = 'md',
  showBackdrop = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  children,
  className,
}: DrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- gate createPortal/document access until after hydration to avoid SSR mismatch
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [isOpen]);

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const isLeft = position === 'left';

  if (!isMounted) return null;

  const drawerContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-modal" role="dialog" aria-modal="true">
          {/* Backdrop with fade animation */}
          {showBackdrop && (
            <motion.div
              className="fixed inset-0 bg-tkf-overlay-dark"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={motionTransition.drawer}
              onClick={handleBackdropClick}
              aria-hidden="true"
            />
          )}

          {/* Drawer Panel with slide animation */}
          <motion.div
            ref={drawerRef}
            className={cn(
              'fixed top-0 h-full border-tkf-line-light bg-tkf-surface shadow-xl',
              sizeStyles[size],
              isLeft ? 'left-0' : 'right-0',
              isLeft ? 'border-r' : 'border-l',
              className
            )}
            initial={{ x: isLeft ? '-100%' : '100%' }}
            animate={{ x: 0 }}
            exit={{ x: isLeft ? '-100%' : '100%' }}
            transition={motionTransition.drawer}
            tabIndex={-1}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(drawerContent, document.body);
}
